import type { SynerallContract } from '@vae/server/src/modules/ExternalAPIs/Synerall/synerallTypes'
import { UpdateCustomerContactInfo } from '@vae/server/src/types/synerallApiTypes'
import { useEffect, useState } from 'react'
import { useContract } from '../../../hooks/useContract'
import { trpc } from '../../../modules/trpc'

/**
 * Hook for object mutation.
 *
 * @param utils - Utility functions for invalidating queries.
 * @returns Mutation object for editing objects.
 */
export const useEditObjectMutation = (utils: any) => {
	return trpc.objects.edit.useMutation({
		onSuccess: () => {
			utils.user.getObjects.invalidate()
		},
	})
}

/**
 * Hook for querying electricity transfer prices.
 *
 * @returns Query object for fetching electricity transfer prices.
 */
export const useElectricityTransferPricesQuery = () => {
	return trpc.electricityTransferPrices.get.useQuery()
}

/**
 * Hook for fetching Synerall customer meter type.
 *
 * @returns Synerall meter type object.
 */
export const useMeteringType = (synerallId: number | null) => {
	return trpc.synerall.getMeteringType.useQuery(
		{ customerId: synerallId ?? 0 },
		{ enabled: !!synerallId }
	)
}

/**
 * Hook for fetching all data needed for the ContractBilling component
 *
 * @param personalId - The user's personal ID
 * @param synerallContractId - The Synerall contract ID
 * @param synerallId - The Synerall object ID
 * @returns All data and functions needed for the ContractBilling component
 */
export const useSyneContractInfo = (
	personalId: string | undefined,
	synerallContractId: number | undefined | null,
	synerallId: number | undefined | null
) => {
	const [isLoading, setIsLoading] = useState(false)

	// Fetch user data from Synerall
	const {
		data: syneUser,
		refetch: refetchSyneUser,
		isLoading: isUserLoading,
	} = trpc.synerall.userByPid.useQuery(
		{
			personalId: personalId || '',
		},
		{
			enabled: Boolean(personalId),
			staleTime: 1000 * 60, // 1 minute
		}
	)

	const { contractData: syneContract, isContractLoading } = useContract()

	// Fetch customer addresses
	const { data: customerAddress, isLoading: isAddressLoading } =
		trpc.synerall.getCustomerAddresses.useQuery(
			{
				customerID: syneUser?.Id || 0,
			},
			{
				enabled: Boolean(syneUser?.Id),
				staleTime: 1000 * 60, // 1 minute
			}
		)

	// Mutation for updating customer language preference
	const editCustomer = trpc.synerall.updateCustomer.useMutation({
		onMutate: () => {
			setIsLoading(true)
		},
		onSuccess: async () => {
			await refetchSyneUser()
			setIsLoading(false)
		},
		onError: () => {
			setIsLoading(false)
		},
	})

	// Mutation for updating customer contacts
	const editContact = trpc.synerall.updateCustomerContactInfo.useMutation({
		onMutate: () => {
			setIsLoading(true)
		},
		onSuccess: async () => {
			await refetchSyneUser()
			setIsLoading(false)
		},
		onError: () => {
			setIsLoading(false)
		},
	})

	// Update isLoading state whenever any query is loading
	useEffect(() => {
		const anyQueryLoading =
			isUserLoading ||
			isContractLoading ||
			isAddressLoading ||
			editCustomer.isLoading ||
			editContact.isLoading
		setIsLoading(anyQueryLoading)
	}, [
		isUserLoading,
		isContractLoading,
		isAddressLoading,
		editCustomer.isLoading,
		editContact.isLoading,
	])

	const updateLanguage = (selectedLanguage: string) => {
		if (synerallContractId && synerallId) {
			editCustomer.mutate({
				synerallId: synerallId,
				synerallContractId: synerallContractId,
				LanguageIsoCode: selectedLanguage,
			})
		}
	}

	const updateContact = (updateInfo: UpdateCustomerContactInfo) => {
		if (!synerallId) return
		editContact.mutate({
			synerallId: synerallId,
			customerID: updateInfo.customerID,
			contactId: updateInfo.contactId,
			Primary: updateInfo.Primary,
			Type: updateInfo.Type,
			Value: updateInfo.Value,
		})
	}

	const hasActiveSupplyContract = !!syneUser?.Contracts?.some(
		(contract: SynerallContract) =>
			contract.Type === 'SupplyContract' && contract.Status === 'Active'
	)

	return {
		syneUser,
		syneContract,
		customerAddress,
		isLoading,
		updateLanguage,
		updateContact,
		hasActiveSupplyContract,
	}
}
